/**
 * Per-deployment configuration for the auth service.
 *
 * This service historically served exactly one deployment (auth.twoshot.app).
 * It is now a multi-app deployable: the SAME image serves TwoShot and Forge
 * (auth.forge.teraplex.io), differing only by configuration. Everything that
 * used to be a hardcoded TwoShot literal — app name, wordmark, colors, page
 * titles, TOS/privacy links, return-to allowlist, CORS host set, SSO cookie
 * name, passkey RP identity, provider set, email strings — routes through
 * this module.
 *
 * THE DEFAULTS BELOW ARE TODAY'S TWOSHOT VALUES, BYTE-FOR-BYTE. With no
 * override configured the service must behave exactly as it did before this
 * module existed (regression-pinned in shared/deployment-config.test.ts —
 * do not "tidy" a default without updating the consumer it mirrors).
 *
 * How the active config is established:
 *   - Server: src/deployment.ts reads the AUTH_DEPLOYMENT_JSON env var
 *     (a partial AuthDeploymentConfig), merges it over the TwoShot defaults
 *     and calls setActiveDeployment() at module load.
 *   - SPA: the server injects `window.__AUTH_CONFIG__` into the served
 *     index.html (src/login/spa-index.ts); web/src/main.tsx adopts it before
 *     first render. With no injection the SPA falls back to these defaults,
 *     i.e. renders TwoShot exactly as before.
 *
 * This module is import-safe from BOTH the server (compiled, .js specifiers)
 * and the Vite SPA (.ts specifiers). It must stay dependency-free and free
 * of process.env reads (the env seam lives in src/deployment.ts).
 */

/** Brand color overrides. Keys mirror shared/branding.ts `colors`; a
 *  deployment overrides only what differs from the TwoShot tokens. */
export interface BrandColorOverrides {
    bg?: string;
    cardSolid?: string;
    cardTransparent?: string;
    cardBorder?: string;
    cardShadow?: string;
    textPrimary?: string;
    textSecondary?: string;
    textMuted?: string;
    textSecondaryHex?: string;
    textMutedHex?: string;
    accent?: string;
    accentActive?: string;
    accentGlow?: string;
    codeBg?: string;
    error?: string;
    errorBg?: string;
    errorText?: string;
    success?: string;
}

/** The brand graphic at the top of the sign-in, consent and connected pages:
 *  an inline SVG path (TwoShot's, shared by the SPA and the server views) or an
 *  image URL (Forge's brand-mark.png — referenced, never embedded).
 *  `wordmark: true` says the graphic already spells the product name, so it is
 *  drawn ALONE; without it the graphic is a symbol drawn beside `appName`. */
export type DeploymentLogo =
    | {kind: "svg"; viewBox: string; path: string; wordmark?: boolean}
    | {kind: "image"; url: string; wordmark?: boolean};

/** How a finished login hands the browser to an external return target
 *  (shared/login-completion.ts):
 *   - "token-fragment": the target gets the first-party access+refresh pair in
 *     its URL fragment. TwoShot's /login/callback is that consumer.
 *   - "relying-party": the target gets no credential. The product signs in only
 *     through its own state/PKCE-bound authorization-code flow (Forge's
 *     /auth/login -> /oauth2/authorize -> /auth/callback), and authorize
 *     continues it from the SSO session the sign-in rail set. defaultExternal is
 *     then the product's login entry. */
export type LoginCompletion = "token-fragment" | "relying-party";
const LOGIN_COMPLETIONS = {"token-fragment": true, "relying-party": true} satisfies Record<LoginCompletion, true>;

export interface ReturnToConfig {
    /** Where a completed login lands when no (valid) return_to was given. */
    defaultExternal: string;
    /** Whether that landing carries tokens. See LoginCompletion. */
    completion: LoginCompletion;
    /** Apex hosts whose https origins (apex + any subdomain) are allowed as
     *  return_to targets AND as CORS origins for the credentialed auth
     *  endpoints. First entry is the primary product host. */
    apexHosts: string[];
    /** Where /login/callback lives on the product frontend. */
    loginCallbackPath: string;
    /** Native app custom scheme ("app.twoshot.mobile:") or null when the
     *  deployment has no native app — all native-handoff paths go inert. */
    nativeScheme: string | null;
    /** Android package id for intent:// URLs (native app only). */
    nativePackageId: string | null;
    /** Origins allowed as native_parent_origin values. */
    nativeParentOrigins: string[];
    /** Default parent origin when none/invalid supplied. */
    defaultParentOrigin: string;
    /** Settings page fallback for the Settings → Connections flow. */
    settingsUrl: string;
}

/** One consent-screen grouping, in render order. A group whose scopes were not
 *  requested renders nothing at all. */
export interface ScopeGroupConfig {
    label: string;
    scopes: string[];
}

/**
 * The deployment's OAuth scope table: what /oauth2/authorize accepts, what an
 * OMITTED `scope` parameter defaults to, and how the consent screen describes
 * each name.
 *
 * WHY THIS IS PER-DEPLOYMENT AND NOT A MODULE CONSTANT. The scope names ARE the
 * product surface. TwoShot's grant access to a media library; Forge's grant
 * access to conversations that run an agent. While this lived in
 * src/oauth2/scopes.ts, the Forge deployment — Forge branding, Forge logo,
 * Forge colours, Forge hosts — still asked its users to share their "audio
 * samples, videos, images, folders & collections", because MCP clients omit
 * `scope` and every default site falls back to the whole set.
 *
 * THREE SITES RESOLVE A REQUESTED SCOPE, and all three call resolveRequestedScopes()
 * below rather than reading this table themselves: src/oauth2/authorize.ts (the
 * consent screen), src/oauth2/consent-submit.ts (the code that is actually stored)
 * and src/oauth2/tokens.ts (the token's `scope` claim). Correct one alone and the
 * screen can look right while the minted token still carries the wrong grant.
 */
export interface ScopeConfig {
    /** Every scope name an authorize request may ASK for, and what the metadata
     *  document advertises as `scopes_supported`. Lowercase only (enforced at
     *  boot), so a name can never collide with the uppercase plan words — ADMIN,
     *  PRO_MAX, ... — that share the token's `scope` claim. */
    valid: string[];
    /** What an authorize request that OMITS `scope` is granted, in the order of
     *  the default scope string. MCP clients routinely omit it, so this list is a
     *  GRANT made without the client asking: a scope left out of it (Forge's
     *  `operator`) is only ever granted when requested by name and consented to.
     *  A non-empty subset of `valid` (enforced at boot). */
    defaults: string[];
    /** The consent-screen phrase for each scope. Must cover every name in
     *  `valid` (enforced by validateScopeConfig). `{app}` is substituted with
     *  `appName` — see src/oauth2/scopes.ts. */
    short: Record<string, string>;
    /** Consent-screen groups, in render order. Every name in `valid` must
     *  appear in exactly one group or it is granted invisibly. */
    groups: ScopeGroupConfig[];
}

export interface PasskeyConfig {
    enabled: boolean;
    /** WebAuthn RP id — the parent domain passkeys bind to. Domain-bound:
     *  a credential registered under one rpId is invisible under another. */
    rpId: string;
    rpName: string;
    /** Web origins allowed to complete WebAuthn ceremonies. */
    origins: string[];
}

/** The Turnstile `action` the phone-code human check is rendered with (web/src/humanCheck.tsx) and required to carry
 *  (src/phone/turnstile.ts): a token solved for anything else, or on another site, is not a pass here. */
export const PHONE_HUMAN_CHECK_ACTION = "phone-code";

export interface PhoneConfig {
    /** Whether this deployment offers phone-code sign-in and signed-in phone verification. */
    enabled: boolean;
    /** ISO-3166 alpha-2 countries accepted by the phone rail, or ["*"] for every country (the
     *  signed design, Forge 7 Oct 2026: every country behind the human check, the limits and the
     *  number check, src/phone/policy.ts, each country treated alike). Empty means none. */
    allowedCountries: string[];
    /** POD-DERIVED, never read from AUTH_DEPLOYMENT_JSON: the allowed countries where this pod can
     *  place a code call (PHONE_VOICE_ALLOWED_COUNTRIES). Set only on the SPA's injected copy
     *  (src/login/spa-index.ts spaConfigFor), where it decides whether "Call me" is offered. */
    voiceCountries?: string[];
}

/** How the sign-in pages look for this deployment. Everything else on the page is shared. */
export interface AppearanceConfig {
    /** The shared kit's orb look behind the form (@twoshot/ui-kit brand): "forge" is the tuned
     *  purple magma, "twoshot" the same magma at TwoShot crimson. */
    orb: "forge" | "twoshot";
    /** "system" follows the device's light/dark setting; "dark" pins dark (a product whose app
     *  is dark-only, so sign-in matches what it leads into). */
    theme: "system" | "dark";
}

/** The tab icon for each colour scheme: `url` replaces faviconUrl and `svgUrl` replaces
 *  faviconSvgUrl while that scheme is active. */
export interface FaviconTheme {
    /** The script that switches them: a byte copy of ui-kit's dist/favicon-theme.js. */
    scriptUrl: string;
    light: {url: string; svgUrl: string};
    dark: {url: string; svgUrl: string};
}

export interface AuthDeploymentConfig {
    /** Product name rendered everywhere ("TwoShot", "Forge"). */
    appName: string;
    /** Product home page (consent "Open <app>" button, logo links). */
    homeUrl: string;
    /** The tab icon every page links through faviconLinks(): a raster icon
     *  (.ico/.png) that every browser can show. */
    faviconUrl: string;
    /** Optional scalable icon, preferred by browsers that show SVG favicons. One set
     *  with faviconUrl: an override that names any icon field names the whole set. */
    faviconSvgUrl?: string;
    /** Optional fixed-ink copies of both icons, one per colour scheme, switched by a script
     *  (ui-kit's favicon-theme.js) at load and whenever the viewer's scheme changes. Chrome keeps
     *  a cached SVG favicon in the colour it was first drawn in, so only a different URL follows a
     *  light/dark change. Without the script the two links above still apply. Part of the set. */
    faviconTheme?: FaviconTheme;
    logo: DeploymentLogo;
    termsUrl: string;
    privacyUrl: string;
    /** Avatar/image CDN base for user image_id rendering ("" = none). */
    imageHost: string;
    /** WWW-Authenticate realm on bearer challenges. */
    bearerRealm: string;
    /** client_id stamped on first-party web-login token pairs. */
    firstPartyClientId: string;
    /** Signup fields; server deployment identity, never a request body client_id. */
    signupProfile: "first-last-username" | "display-name";
    /**
     * Whether access tokens expose end-user profile/contact claims (email, name, phone).
     * FALSE is TwoShot's historical token surface; Forge opts in. It also gates the
     * user_phone join in the token-mint query, so a deployment whose database lacks the
     * phone metadata columns never queries them.
     */
    identityClaims: boolean;
    /** SSO cookie name + default domain (SSO_COOKIE_DOMAIN env still wins). */
    cookie: {name: string; defaultDomain: string};
    returnTo: ReturnToConfig;
    passkey: PasskeyConfig;
    /** OAuth provider buttons the SPA renders AND the server registry
     *  accepts, in render order. Providers absent from this list are not
     *  mounted even if credentials are configured. */
    providers: string[];
    /** Whether the email OTP flow is offered in the SPA. */
    emailEnabled: boolean;
    /** Phone-code sign-in / signed-in phone verification (Telnyx Verify). */
    phone: PhoneConfig;
    /** Whether this deployment's database carries the referral/credit schema:
     *  credit_transaction, referral_reward, user_ip_activity, short_link,
     *  short_link_redemption, generation_job.
     *
     *  FALSE IS NOT A FEATURE TOGGLE, IT IS A SCHEMA FACT. The referral reward
     *  path runs INSIDE the signup transaction, and its user_ip_activity and
     *  referral_reward queries are not individually wrapped, so on a database
     *  without those relations a signup carrying ?referrer_id= raises 42P01,
     *  the transaction rolls back and the signup fails with a 500.
     *  forge_auth (migration-identity/V1) has none of the six, and Forge's own
     *  referral lives in the Forge product database under its own vesting
     *  rules -- not here. */
    referralRewards: boolean;
    /** OAuth scope table: accepted names, the omitted-`scope` default, and the
     *  consent-screen copy. */
    scopes: ScopeConfig;
    /** Brand color overrides over shared/branding.ts tokens. */
    colors?: BrandColorOverrides;
    /** The orb backdrop and theme rule of the sign-in pages. */
    appearance: AppearanceConfig;
}

/** Forge's look, also what a forge-web manifest that omits `appearance` gets (mergeDeploymentConfig). */
export const FORGE_APPEARANCE: AppearanceConfig = {orb: "forge", theme: "system"};

/** TwoShot's approved wordmark (11 Oct 2026): the viewBox and path data of
 *  twoshot-wordmark-white.svg in the approved brand set, copied exactly and never
 *  redrawn (shared/deployment-config.test.ts rebuilds the committed copy,
 *  shared/brand/twoshot-wordmark-white.svg, from these two). It spells the name, so
 *  it is drawn alone: "logo and bullet never together". */
export const TWOSHOT_WORDMARK_VIEWBOX = "-4 -4 661.96 234.46";
export const TWOSHOT_WORDMARK_PATH =
    "M5.15 2.43L139.75 2.43L145.78 49.07L171.28 2.43L205.02 2.43L213.65 49.07L236.55 2.43L273.43 2.43L222.89 102.43L188.51 102.43L179.8 48.58L151.59 102.43L117.21 102.43L106.95 30.88L68.81 30.88L55.86 102.43L19.91 102.43L32.86 30.88L0 30.88L5.15 2.43ZM385.34 52.43C391.1 20.6 371.5 0 335.45 0C299.4 0 272.34 20.6 266.58 52.43C260.82 84.26 280.42 104.86 316.47 104.86C352.52 104.86 379.58 84.26 385.34 52.43ZM166.59 183.95C166.3 185.18 165.85 186.92 165.6 188.28C164.39 194.96 164.56 201.71 167.54 207.25C176.47 223.87 196.82 226.36 215.9 226.36C238.76 226.36 272.63 220.3 277.89 191.23C285.16 151.09 218.48 162.38 211.76 155.1C211.29 154.58 211.48 153.9 211.59 153.26C212.6 147.71 224.83 147.21 228.55 147.21C231.93 147.21 236.04 147.55 239 148.71C244.78 150.97 243.8 154.81 244.33 160.51L278.17 160.51C279.87 156.52 279.81 158.44 277.2 159.94C277.1 160 276.24 160.28 276.24 160.25C276.27 160.09 279.28 160.04 279.89 156.68C284.87 129.14 255.69 121.54 233.42 121.54C215.35 121.54 192.05 125.47 179.36 142.85C176.89 146.22 174.5 150.8 173.8 154.67C166.41 195.54 232.79 182.85 239.06 191.1C239.59 191.79 239.39 192.57 239.24 193.4C238.1 199.69 225.5 200.17 221.27 200.17C216.85 200.17 211.45 199.72 207.57 198.19C200.45 195.36 202.26 190.96 202.11 183.95L166.59 183.95ZM306.93 124.03L342.88 124.03L336.86 157.31L371.21 157.31L377.23 124.03L413.18 124.03L395.08 224.03L359.13 224.03L366.06 185.75L331.71 185.75L324.78 224.03L288.83 224.03L306.93 124.03ZM538.89 174.03C544.65 142.2 525.05 121.6 489 121.6C452.95 121.6 425.89 142.2 420.13 174.03C414.37 205.86 433.97 226.46 470.02 226.46C506.07 226.46 533.13 205.86 538.89 174.03ZM552.28 124.03L653.96 124.03L648.81 152.48L615.95 152.48L602.99 224.03L567.04 224.03L580 152.48L547.13 152.48L552.28 124.03Z";

/** Today's production values, exactly. */
export const TWOSHOT_DEPLOYMENT: AuthDeploymentConfig = {
    appName: "TwoShot",
    homeUrl: "https://twoshot.app/",
    // The approved icon set, shipped in web/public and served by this host under /login/:
    // the SVG follows the system light/dark setting; favicon.ico (16/32/48) is the fallback.
    // The script points both links at fixed black or white copies for the current scheme.
    faviconUrl: "/login/favicon.ico",
    faviconSvgUrl: "/login/twoshot-favicon.svg",
    faviconTheme: {
        scriptUrl: "/login/favicon-theme.js",
        light: {url: "/login/favicon.ico", svgUrl: "/login/twoshot-favicon-black.svg"},
        dark: {url: "/login/twoshot-favicon-white-32.png", svgUrl: "/login/twoshot-favicon-white.svg"},
    },
    logo: {kind: "svg", viewBox: TWOSHOT_WORDMARK_VIEWBOX, path: TWOSHOT_WORDMARK_PATH, wordmark: true},
    termsUrl: "https://twoshot.app/terms",
    privacyUrl: "https://twoshot.app/privacy",
    imageHost: "https://i.twoshot.app",
    bearerRealm: "twoshot",
    firstPartyClientId: "twoshot-web",
    signupProfile: "first-last-username",
    identityClaims: false,
    cookie: {name: "twoshot_session", defaultDomain: ".twoshot.app"},
    returnTo: {
        defaultExternal: "https://twoshot.app/login/callback",
        completion: "token-fragment",
        apexHosts: ["twoshot.app"],
        loginCallbackPath: "/login/callback",
        nativeScheme: "app.twoshot.mobile:",
        nativePackageId: "app.twoshot.mobile",
        nativeParentOrigins: ["https://twoshot.app", "capacitor://twoshot.app"],
        defaultParentOrigin: "https://twoshot.app",
        settingsUrl: "https://twoshot.app/settings",
    },
    passkey: {
        enabled: true,
        rpId: "twoshot.app",
        rpName: "TwoShot",
        origins: ["https://auth.twoshot.app", "https://twoshot.app"],
    },
    providers: ["instagram", "discord", "google", "apple"],
    emailEnabled: true,
    phone: {enabled: false, allowedCountries: []},
    appearance: {orb: "twoshot", theme: "dark"},
    referralRewards: true,
    // Verbatim from the historical src/oauth2/scopes.ts module constants
    // (VALID_SCOPES / SCOPE_SHORT / SCOPE_GROUPS). `{app}` reproduces that
    // file's `use the ${deployment.appName} AI assistant` interpolation, so a
    // deployment that overrides only appName still renders its own name.
    scopes: {
        valid: [
            "samples:read",
            "videos:read",
            "images:read",
            "library:read",
            "content:import",
            "assistant",
            "profile:read",
        ],
        // TwoShot grants its whole table to a client that omits `scope`, as it always has.
        defaults: [
            "samples:read",
            "videos:read",
            "images:read",
            "library:read",
            "content:import",
            "assistant",
            "profile:read",
        ],
        short: {
            "samples:read": "audio samples",
            "videos:read": "videos",
            "images:read": "images",
            "library:read": "folders & collections",
            "content:import": "import media from URLs",
            "assistant": "use the {app} AI assistant",
            "profile:read": "profile and credit balance",
        },
        groups: [
            {label: "View your library", scopes: ["samples:read", "videos:read", "images:read", "library:read"]},
            {label: "Take actions", scopes: ["content:import", "assistant"]},
            {label: "View your profile", scopes: ["profile:read"]},
        ],
    },
};

/** Deep-ish merge: one level of section merging (returnTo/passkey/cookie/
 *  colors merge per-key; arrays and scalars replace wholesale). A partial
 *  override therefore only needs the keys that differ. */
export function mergeDeploymentConfig(
    base: AuthDeploymentConfig,
    partial: Partial<AuthDeploymentConfig> | null | undefined,
): AuthDeploymentConfig {
    if (!partial) return base;
    return {
        ...base,
        ...partial,
        // Existing Forge manifests already name both trusted identity fields. Derive
        // their policy when omitted; an explicit contradictory policy fails at boot.
        signupProfile: partial.signupProfile ?? (
            (partial.appName ?? base.appName) === "Forge"
            && (partial.firstPartyClientId ?? base.firstPartyClientId) === "forge-web"
                ? "display-name" : base.signupProfile
        ),
        // ONE icon set, replaced whole like `logo`: an override that names any icon field
        // names all of them, so another brand never keeps TwoShot's SVG beside its own icon
        // (browsers prefer the SVG, so it would win the tab), nor TwoShot's theme copies
        // (the script would swap its icon for TwoShot's). An SVG named without a raster
        // icon leaves faviconUrl empty, which validateDeploymentConfig refuses.
        ...(partial.faviconUrl !== undefined || partial.faviconSvgUrl !== undefined || partial.faviconTheme !== undefined
            ? {faviconUrl: partial.faviconUrl ?? "", faviconSvgUrl: partial.faviconSvgUrl, faviconTheme: partial.faviconTheme}
            : {}),
        cookie: {...base.cookie, ...(partial.cookie ?? {})},
        returnTo: {
            ...base.returnTo,
            ...(partial.returnTo ?? {}),
            // forge-web signs in only through its own PKCE code flow, so a Forge manifest that
            // omits the mode must not inherit TwoShot's token-fragment (validated at boot).
            completion: partial.returnTo?.completion ?? (
                (partial.firstPartyClientId ?? base.firstPartyClientId) === "forge-web"
                    ? "relying-party" : base.returnTo.completion
            ),
        },
        passkey: {...base.passkey, ...(partial.passkey ?? {})},
        phone: {...base.phone, ...(partial.phone ?? {})},
        // Like completion above: a forge-web manifest that omits it must not inherit TwoShot's
        // crimson, dark-only page.
        appearance: {
            ...((partial.firstPartyClientId ?? base.firstPartyClientId) === "forge-web" && partial.appearance === undefined
                ? FORGE_APPEARANCE : base.appearance),
            ...(partial.appearance ?? {}),
        },
        colors: partial.colors ? {...(base.colors ?? {}), ...partial.colors} : base.colors,
        logo: partial.logo ?? base.logo,
        providers: partial.providers ?? base.providers,
        // WHOLESALE, never per-key: a deployment that names its own scopes names
        // ALL of them. Merging `short` key-by-key would leave TwoShot's
        // descriptions sitting beside another product's scope names.
        scopes: partial.scopes ?? base.scopes,
    };
}

/** The primary product host used when an integration needs this deployment's
 * identity but has no user-supplied email. Empty means the deployment has no
 * safe identity of its own; never fall back to another product's domain. */
export function primaryProductHost(cfg: AuthDeploymentConfig): string {
    const host = cfg.returnTo.apexHosts[0]?.trim();
    if (!host) {
        throw new Error("AUTH_DEPLOYMENT_JSON returnTo.apexHosts must name a primary product host");
    }
    return host;
}

/** A scope name: lowercase letters, digits and `:._-`, starting with a letter. */
const SCOPE_NAME = /^[a-z][a-z0-9:._-]*$/;

/**
 * Reject a misconfigured scope table LOUDLY, at boot, beside the
 * AUTH_DEPLOYMENT_JSON parse that already fails the same way.
 *
 * Every fault below is otherwise SILENT and user-visible: a scope with no
 * description renders as a blank permission line, and a scope no group names is
 * granted without ever appearing on the consent screen at all. Returns `cfg` so
 * it can wrap the merge inline.
 */
export function validateScopeConfig(cfg: AuthDeploymentConfig): AuthDeploymentConfig {
    const {valid, defaults, short, groups} = cfg.scopes;
    const where = "AUTH_DEPLOYMENT_JSON scopes";
    if (valid.length === 0) {
        throw new Error(`${where}.valid is empty — every authorize request would be refused with invalid_scope`);
    }
    const malformed = valid.filter(s => !SCOPE_NAME.test(s));
    if (malformed.length > 0) {
        throw new Error(`${where}.valid has names that are not lowercase scope tokens: ${malformed.join(", ")} — ` +
            "an uppercase name could collide with a plan word (ADMIN, PRO_MAX, ...) in the token's scope claim");
    }
    const repeated = valid.filter((s, i) => valid.indexOf(s) !== i);
    if (repeated.length > 0) {
        throw new Error(`${where}.valid names a scope twice: ${repeated.join(", ")}`);
    }
    // `defaults` is required: a table without it would leave "what does an omitted scope
    // grant?" to whichever fallback a call site invented, which is how a scope meant to be
    // explicit-only ends up granted to every client that asks for nothing.
    if (!Array.isArray(defaults) || defaults.length === 0) {
        throw new Error(`${where}.defaults must name what an omitted scope grants — every client that omits scope would be refused`);
    }
    const strayDefaults = defaults.filter(s => !valid.includes(s));
    if (strayDefaults.length > 0) {
        throw new Error(`${where}.defaults names scopes absent from scopes.valid: ${strayDefaults.join(", ")}`);
    }
    const repeatedDefaults = defaults.filter((s, i) => defaults.indexOf(s) !== i);
    if (repeatedDefaults.length > 0) {
        throw new Error(`${where}.defaults names a scope twice: ${repeatedDefaults.join(", ")}`);
    }
    const undescribed = valid.filter(s => !short[s]);
    if (undescribed.length > 0) {
        throw new Error(`${where}.short has no description for: ${undescribed.join(", ")} — the consent screen would render a blank permission`);
    }
    const grouped = new Set(groups.flatMap(g => g.scopes));
    const ungrouped = valid.filter(s => !grouped.has(s));
    if (ungrouped.length > 0) {
        throw new Error(`${where}.groups never renders: ${ungrouped.join(", ")} — those scopes would be granted invisibly`);
    }
    const unknown = [...grouped].filter(s => !valid.includes(s));
    if (unknown.length > 0) {
        throw new Error(`${where}.groups names scopes absent from scopes.valid: ${unknown.join(", ")}`);
    }
    return cfg;
}

/** Validate the deployment as a whole. Keep this as the server boot gate: a
 * missing primary host would otherwise send phone-only reputation checks under
 * another product's identity, while a broken scope table silently grants or
 * renders the wrong permissions. */
/** Whether the phone rail serves numbers from `country` at all (ISO alpha-2). The one reading of
 *  allowedCountries, shared by the server's policy and the sign-in page's country hint. */
export function phoneCountryAdmitted(phone: {readonly allowedCountries: readonly string[]}, country: string): boolean {
    return phone.allowedCountries.includes("*") || phone.allowedCountries.includes(country);
}

export function validateDeploymentConfig(cfg: AuthDeploymentConfig): AuthDeploymentConfig {
    primaryProductHost(cfg);
    if (typeof cfg.faviconUrl !== "string" || cfg.faviconUrl === "") {
        throw new Error("AUTH_DEPLOYMENT_JSON faviconUrl must name a raster icon whenever the icon set is overridden (the set is replaced whole)");
    }
    validateFaviconTheme(cfg);
    validateSignupProfileConfig(cfg);
    const everyCountry = cfg.phone.allowedCountries.includes("*");
    if (everyCountry && cfg.phone.allowedCountries.length !== 1) {
        throw new Error('AUTH_DEPLOYMENT_JSON phone.allowedCountries is ["*"] alone (every country) or a list of countries, never both');
    }
    for (const country of everyCountry ? [] : cfg.phone.allowedCountries) {
        if (!/^[A-Z]{2}$/.test(country)) {
            throw new Error(`AUTH_DEPLOYMENT_JSON phone.allowedCountries contains ${JSON.stringify(country)}; expected ISO-3166 alpha-2 uppercase`);
        }
    }
    if ("uncheckedCountries" in cfg.phone) {
        // Removed 21 Oct 2026 (owner: "we can't just block countries"): with no check able to run, every country is
        // refused alike. Refused rather than ignored, so a stale manifest cannot look as if it still did something.
        throw new Error("AUTH_DEPLOYMENT_JSON phone.uncheckedCountries no longer exists: every country is treated alike; delete the key");
    }
    if (cfg.phone.enabled && cfg.phone.allowedCountries.length === 0) {
        throw new Error("AUTH_DEPLOYMENT_JSON phone.enabled requires at least one allowed country");
    }
    if (cfg.appearance.orb !== "forge" && cfg.appearance.orb !== "twoshot") {
        throw new Error(`AUTH_DEPLOYMENT_JSON appearance.orb is ${JSON.stringify(cfg.appearance.orb)}; expected "forge" or "twoshot"`);
    }
    if (cfg.appearance.theme !== "system" && cfg.appearance.theme !== "dark") {
        throw new Error(`AUTH_DEPLOYMENT_JSON appearance.theme is ${JSON.stringify(cfg.appearance.theme)}; expected "system" or "dark"`);
    }
    if (cfg.phone.enabled && !cfg.identityClaims) {
        throw new Error("AUTH_DEPLOYMENT_JSON phone.enabled requires identityClaims (the phone rail's metadata columns are read only on that path)");
    }
    for (const apex of cfg.returnTo.apexHosts) assertApexHostShape(apex);
    // Both fallbacks, gated on identical terms. defaultExternal is where a
    // LOGIN lands (with the user's tokens in the fragment, under
    // token-fragment completion); settingsUrl
    // is where the authenticated CONNECT flow lands. Neither may name a host
    // this deployment's own allowlist would refuse.
    assertReturnToUrlAdmitted(cfg.returnTo.defaultExternal, "defaultExternal", cfg);
    assertReturnToUrlAdmitted(cfg.returnTo.settingsUrl, "settingsUrl", cfg);
    validateLoginCompletionConfig(cfg);
    return validateScopeConfig(cfg);
}

/** The manifest is JSON, so a misspelt mode must stop the boot rather than reach
 *  the completion rule. Forge (forge-web) has no fragment consumer, so an explicit
 *  token-fragment there would put the user's tokens in a URL nothing reads, and is
 *  refused (an omitted mode is derived as relying-party by mergeDeploymentConfig).
 *  The native app hand-off exists only to deliver the token pair to an app, so a
 *  relying-party deployment cannot also name a native app. */
export function validateLoginCompletionConfig(cfg: AuthDeploymentConfig): AuthDeploymentConfig {
    const completion: unknown = cfg.returnTo.completion;
    if (typeof completion !== "string" || !Object.hasOwn(LOGIN_COMPLETIONS, completion)) {
        throw new Error(`AUTH_DEPLOYMENT_JSON returnTo.completion is ${JSON.stringify(completion)}; expected one of ${Object.keys(LOGIN_COMPLETIONS).join(", ")}`);
    }
    if (cfg.firstPartyClientId === "forge-web" && completion !== "relying-party") {
        throw new Error("AUTH_DEPLOYMENT_JSON returnTo.completion must be relying-party for forge-web: Forge has no token-fragment consumer");
    }
    if (completion === "relying-party" && cfg.returnTo.nativeScheme !== null) {
        throw new Error("AUTH_DEPLOYMENT_JSON returnTo.completion relying-party cannot name a nativeScheme: the native hand-off delivers tokens");
    }
    return cfg;
}

/** A request cannot opt into Forge's simpler signup by supplying a client_id.
 * Both server-owned deployment identity fields must agree with the field policy. */
export function validateSignupProfileConfig(cfg: AuthDeploymentConfig): AuthDeploymentConfig {
    const forgeBrand = cfg.appName === "Forge";
    const forgeClient = cfg.firstPartyClientId === "forge-web";
    if (forgeBrand !== forgeClient
        || (cfg.signupProfile !== "display-name" && cfg.signupProfile !== "first-last-username")
        || (cfg.signupProfile === "display-name") !== (forgeBrand && forgeClient)) {
        throw new Error("AUTH_DEPLOYMENT_JSON signupProfile must agree with appName and firstPartyClientId (Forge / forge-web uses display-name)");
    }
    return cfg;
}

/** An apex host is interpolated into a RegExp by isAllowedCorsOrigin, so a
 *  value carrying regex metacharacters is a CORS allowlist admitting more
 *  than it names. Reject it at boot rather than letting it widen the
 *  allowlist silently — a hostname legitimately contains none of these. */
export function assertApexHostShape(apex: string): void {
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*$/.test(apex)) {
        throw new Error(
            `AUTH_DEPLOYMENT_JSON returnTo.apexHosts contains ${JSON.stringify(apex)}, which ` +
            `is not a plain lowercase hostname. It builds the CORS allowlist, so a value ` +
            `carrying regex metacharacters would admit origins it does not name.`,
        );
    }
}

/** A returnTo URL the deployment FALLS BACK to must itself be a host this
 *  deployment admits.
 *
 *  These fields are silent when wrong. `mergeDeploymentConfig` merges
 *  `returnTo` key-by-key, so a manifest that names its own `apexHosts` but
 *  omits one of these INHERITS TwoShot's value — the deployment then boots
 *  clean and sends its own users to another product's page. The realistic way
 *  in is a hand-edited manifest that moves apexHosts to a new product and
 *  leaves a fallback on the old one.
 *
 *  Takes the field NAME so each fallback is gated on identical terms:
 *  `settingsUrl` is where the authenticated connect flow lands, and
 *  `defaultExternal` is where a login that cannot resolve a return target
 *  falls back to — and under token-fragment completion THAT redirect carries
 *  the access and refresh tokens in its fragment, so a host outside apexHosts
 *  hands this deployment's own
 *  users' tokens to an origin it does not own. Loud at boot, like the other
 *  checks here: a misconfigured security allowlist must never fail open into
 *  another deployment's host set. */
export function assertReturnToUrlAdmitted(
    value: string,
    field: string,
    cfg: AuthDeploymentConfig,
): void {
    const where = `AUTH_DEPLOYMENT_JSON returnTo.${field}`;
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        throw new Error(`${where} is not a valid URL: ${value}`);
    }
    if (!isAllowedProductHostUrl(url, cfg)) {
        throw new Error(
            `${where} (${value}) is not an https URL on one of returnTo.apexHosts ` +
            `(${cfg.returnTo.apexHosts.join(", ")}). This deployment would send its own ` +
            `users to a host it does not own, and its own allowlist would refuse the ` +
            `very URL it is about to use.`,
        );
    }
}

/**
 * The accepted scope names in a space-separated scope string: names this table
 * accepts, each once, in request order. Everything else is DROPPED, never copied
 * through — the token's `scope` claim holds these beside the plan words /validate
 * reads, so an unfiltered request word such as `ADMIN` would be a privilege
 * grant. Used directly where an explicit (possibly empty) scope must stay as asked:
 * the token mint, where `""` is a first-party session that carries no scope.
 *
 * TOTAL over its input: anything that is not a string accepts nothing. A query
 * parameter repeated in a URL (`?scope=a&scope=b`) arrives as an array, and that
 * is a request this deployment cannot read, not one to throw on.
 */
export function acceptedScopes(table: Pick<ScopeConfig, "valid">, scope: unknown): string[] {
    if (typeof scope !== "string") return [];
    const out: string[] = [];
    for (const name of scope.split(" ")) {
        if (table.valid.includes(name) && !out.includes(name)) out.push(name);
    }
    return out;
}

/**
 * What a PERSISTED grant may vouch for. Old consents, codes and refresh tokens
 * copied unaccepted words verbatim; registering a new explicit-only name must
 * not turn those words into permission. Only a grant written by the resolver
 * (server record / signed claim `scope_resolved: true`) may retain explicit-only
 * names. Unmarked grants keep accepted defaults; no default is added.
 */
export function acceptedGrantScopes(table: Pick<ScopeConfig, "valid" | "defaults">, scope: unknown, resolved: unknown): string[] {
    const accepted = acceptedScopes(table, scope);
    return resolved === true ? accepted : accepted.filter(name => table.defaults.includes(name));
}

/**
 * What an authorize request's `scope` parameter is granted. Omitted (absent or
 * empty) grants `defaults`; otherwise acceptedScopes(). An empty result means
 * nothing the deployment accepts was asked for: the caller refuses with
 * invalid_scope. The ONE resolver for /oauth2/authorize, the consent submit and
 * the token mint, so the screen, the stored code and the token cannot disagree.
 */
export function resolveRequestedScopes(table: Pick<ScopeConfig, "valid" | "defaults">, scope: unknown): string[] {
    const omitted = scope === undefined || scope === null || scope === "";
    return omitted ? [...table.defaults] : acceptedScopes(table, scope);
}

/**
 * The consent-screen phrase for each scope, with `{app}` resolved to this
 * deployment's app name.
 *
 * Lives here rather than inline in src/oauth2/scopes.ts so the substitution is
 * ONE implementation that both the server and its tests call — a test that
 * re-implemented `{app}` would pin a copy and stay green while the real one
 * broke. It reproduces this module's historical
 * `use the ${deployment.appName} AI assistant`, so a deployment overriding only
 * `appName` still renders its own name rather than "TwoShot".
 */
export function resolveScopeShort(cfg: AuthDeploymentConfig): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [scope, phrase] of Object.entries(cfg.scopes.short)) {
        out[scope] = phrase.split("{app}").join(cfg.appName);
    }
    return out;
}

// --- Active-deployment registry -------------------------------------------
//
// shared/ modules (login-return-to.ts) and the SPA cannot import the server's
// env seam, so they read the active config lazily through here. The server
// (src/deployment.ts) and the SPA bootstrap (web/src/main.tsx) each call
// setActiveDeployment() once at startup; until then — and forever, for a
// plain TwoShot deployment — the TwoShot defaults apply.

let active: AuthDeploymentConfig | null = null;

export function setActiveDeployment(cfg: AuthDeploymentConfig): void {
    active = cfg;
}

export function activeDeployment(): AuthDeploymentConfig {
    return active ?? TWOSHOT_DEPLOYMENT;
}

// --- Derived helpers (pure; take an explicit cfg so they are testable and
// --- usable against non-active configs) -----------------------------------

/** Is `origin` allowed to make credentialed CORS calls to the auth API?
 *  https://<apex> or https://<any-sub>.<apex> for each configured apex host.
 *  (Reproduces the historical ^https://([a-z0-9-]+\.)*twoshot\.app$ check.) */
export function isAllowedCorsOrigin(origin: string, cfg: AuthDeploymentConfig): boolean {
    for (const apex of cfg.returnTo.apexHosts) {
        // EVERY metacharacter, not just the dot. An apex is interpolated into a
        // RegExp, so escaping only "." leaves a config-supplied "|" acting as
        // alternation: apexHosts ["twoshot.app|evil.com"] would admit evil.com.
        // Valid hostnames contain none of these, so this is a no-op for every
        // real config and makes a malformed one match literally (never).
        // assertApexHostShape rejects such a host at boot; this is the belt.
        const escaped = apex.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        if (new RegExp(`^https://([a-z0-9-]+\\.)*${escaped}$`).test(origin)) return true;
    }
    return false;
}

/** Is `u` an https URL on a configured apex host or one of its subdomains? */
export function isAllowedProductHostUrl(u: URL, cfg: AuthDeploymentConfig): boolean {
    if (u.protocol !== "https:") return false;
    return cfg.returnTo.apexHosts.some(apex => u.hostname === apex || u.hostname.endsWith(`.${apex}`));
}

/** Where an authenticated "connect a provider" flow may send the browser
 *  when it finishes. The flow lands on the deployment's own settings page,
 *  so the whole product host is in scope rather than one path — but ONLY
 *  this deployment's hosts: an unvalidated return_to is an open redirect
 *  on the auth origin, and a hardcoded default sends one deployment's
 *  users to another's settings page.
 *
 *  Anything else — a foreign origin, http, a javascript: URI, a malformed
 *  string, a missing value — resolves to this deployment's settings URL.
 *  Local dev origins are deliberately NOT admitted: these endpoints are
 *  reachable cross-origin only through isAllowedCorsOrigin above, which
 *  admits no http origin, so a localhost caller never gets this far. */
export function resolveConnectReturnTo(raw: unknown, cfg: AuthDeploymentConfig): string {
    if (typeof raw !== "string" || raw === "") return cfg.returnTo.settingsUrl;
    try {
        const u = new URL(raw);
        return isAllowedProductHostUrl(u, cfg) ? u.toString() : cfg.returnTo.settingsUrl;
    } catch {
        return cfg.returnTo.settingsUrl;
    }
}

/** The page title of the SPA ("Sign in to TwoShot"). */
export function signInTitle(cfg: AuthDeploymentConfig): string {
    return `Sign in to ${cfg.appName}`;
}

/** A theme names both schemes' copies of both icons, beside an SVG icon they replace. */
function validateFaviconTheme(cfg: AuthDeploymentConfig): void {
    const theme: unknown = cfg.faviconTheme;
    if (theme === undefined) return;
    const where = "AUTH_DEPLOYMENT_JSON faviconTheme";
    if (!cfg.faviconSvgUrl) throw new Error(`${where} needs faviconSvgUrl: it switches the SVG icon and the raster icon together`);
    const t = (typeof theme === "object" && theme !== null ? theme : {}) as Partial<Record<"scriptUrl" | "light" | "dark", unknown>>;
    const scheme = (v: unknown) => (typeof v === "object" && v !== null ? v : {}) as Partial<Record<"url" | "svgUrl", unknown>>;
    const urls: [string, unknown][] = [["scriptUrl", t.scriptUrl], ["light.url", scheme(t.light).url],
        ["light.svgUrl", scheme(t.light).svgUrl], ["dark.url", scheme(t.dark).url], ["dark.svgUrl", scheme(t.dark).svgUrl]];
    const missing = urls.filter(([, url]) => typeof url !== "string" || url === "").map(([name]) => name);
    if (missing.length > 0) throw new Error(`${where} must name every URL; missing or empty: ${missing.join(", ")}`);
}

/** The tab-icon tags in the <head> of every page this deployment serves (the SPA shell
 *  through src/login/spa-index.ts, the server views through src/deployment.ts): one
 *  derivation, so no page can keep another brand's icon. The SVG comes first; beside it the
 *  raster icon is marked sizes="32x32", without which Chromium picks the raster over the SVG;
 *  a raster icon on its own gets no size hint it may not match. A theme adds each link's
 *  light/dark URLs and, after both links, the script that applies them (deferred: it never
 *  holds up the page). */
export function faviconLinks(cfg: AuthDeploymentConfig): string[] {
    const svg = cfg.faviconSvgUrl;
    const theme = svg ? cfg.faviconTheme : undefined;
    const themed = (scheme: "url" | "svgUrl") => theme
        ? ` data-favicon-light="${escapeAttr(theme.light[scheme])}" data-favicon-dark="${escapeAttr(theme.dark[scheme])}"`
        : "";
    const raster = `<link rel="icon" href="${escapeAttr(cfg.faviconUrl)}"${svg ? ' sizes="32x32"' : ""}${themed("url")}/>`;
    if (!svg) return [raster];
    const vector = `<link rel="icon" type="image/svg+xml" href="${escapeAttr(svg)}"${themed("svgUrl")}/>`;
    return theme ? [vector, raster, `<script src="${escapeAttr(theme.scriptUrl)}" defer></script>`] : [vector, raster];
}

/** Header wordmark height in CSS px: two lines of capitals about as tall as the old
 *  lockup's 24px name. */
export const WORDMARK_HEIGHT_PX = 44;

/** The header logo's size in CSS px, shared by the SPA and the server views so every
 *  page draws the same header. A symbol keeps its historical 40x24 box beside the
 *  name. A wordmark is drawn WORDMARK_HEIGHT_PX tall in its own proportions (an
 *  image's width is left to the image: null). */
export function logoBox(logo: DeploymentLogo): {width: number | null; height: number} {
    if (!logo.wordmark) return {width: 40, height: 24};
    if (logo.kind === "image") return {width: null, height: WORDMARK_HEIGHT_PX};
    const [, , w, h] = logo.viewBox.trim().split(/[\s,]+/).map(Number);
    const width = w > 0 && h > 0 ? Math.round(WORDMARK_HEIGHT_PX * w / h * 100) / 100 : null;
    return {width, height: WORDMARK_HEIGHT_PX};
}

function escapeAttr(s: string): string {
    return s.replace(/[&<>"']/g, c => ({"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"}[c]!));
}
